import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { filter, floor, isEmpty } from 'lodash';

import { fetch as axios } from '../../axios_config';

import { handleRejectedResponse } from './utils';
import { setPackingPreferences } from './post-shipment';

export const CreateWorkOrder = createAsyncThunk(
  'wo/create-work-order',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/wo/create-work-order`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Create Work Order', rejectWithValue);
    }
  }
);

export const MergeWorkOrders = createAsyncThunk(
  'wo/merge-work-orders',
  async ({ workOrderIds, orderName }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/wo/merge-work-orders`,
        { workOrderIds, orderName }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Merge Work Orders', rejectWithValue);
    }
  }
);

export const GetPoSkusForWo = createAsyncThunk(
  'wo/list-po-skus-for-wo',
  async ({ po_id, exportAll = false, page, sku_ids } = {}, { rejectWithValue }) => {
    try {
      const params = { po_id };
      if (exportAll) params.exportAll = true;
      if (page) params.page = page;
      if (Array.isArray(sku_ids) && sku_ids.length) params.sku_ids = JSON.stringify(sku_ids);
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/list-po-skus-for-wo`,
        { params }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'List PO SKUs for Work Order', rejectWithValue);
    }
  }
);

export const AddPoToDraftWorkOrder = createAsyncThunk(
  'wo/add-from-po',
  async ({ workOrderId, po_id, sku_ids }, { rejectWithValue }) => {
    try {
      const body = { po_id };
      if (Array.isArray(sku_ids) && sku_ids.length) body.sku_ids = sku_ids;
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/wo/add-from-po/${workOrderId}`,
        body
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Add PO to Draft Work Order', rejectWithValue);
    }
  }
);



export const UpdateShipQuantity = createAsyncThunk(
  'wo/edit-ship-quantity',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/edit-ship-quantity`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Ship Quantity', rejectWithValue);
    }
  }
);

export const UpdateOrderItem = createAsyncThunk(
  'wo/edit-order-item',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/edit-order-item`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Order Item', rejectWithValue);
    }
  }
);

export const EditBulkOrderItemExpiry = createAsyncThunk(
  'wo/edit-bulk-order-item-expiry',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/edit-bulk-order-item-expiry`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Edit Bulk Order Items Expiry', rejectWithValue);
    }
  }
);

export const UpdateOrderDetails = createAsyncThunk(
  'wo/edit-order-details',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/edit-order-details`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Order Details', rejectWithValue);
    }
  }
);

export const AddOrderItems = createAsyncThunk(
  'wo/add-order-items/:work_order_id',
  async (data, { rejectWithValue }) => {
    try {
      const { workOrderId } = data || {};
      delete data.workOrderId;

      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/wo/add-order-items/${workOrderId}`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Add Order Items', rejectWithValue);
    }
  }
);

export const GetAllShipFromAddress = createAsyncThunk(
  'wo/all-warehouses',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/all-warehouses`,
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get All Ship-From-Address', rejectWithValue);
    }
  }
);

export const GetAllUsers = createAsyncThunk(
  'wo/all-users',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/all-users`,
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get All Users', rejectWithValue);
    }
  }
);

export const GetUserWorkOrders = createAsyncThunk(
  'wo/user-work-orders',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/user-work-orders`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Work Orders', rejectWithValue);
    }
  }
);

export const GetWarehouseWorkOrders = createAsyncThunk(
  'wo/warehouse-orders',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/warehouse-orders`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Work Orders', rejectWithValue);
    }
  }
);

export const GetOrderItems = createAsyncThunk(
  'wo/order-items',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/order-items`,
        {
          params: data
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Order Items', rejectWithValue);
    }
  }
);

export const GetRemainingSkuList = createAsyncThunk(
  'wo/all-remaining-skus',
  async (data, { rejectWithValue }) => {
    try {
      const { selectedOrderItems = [] } = data
      delete data.selectedOrderItems
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/all-remaining-skus`,
        {
          params: data
        }
      );
      return { ...response?.data, extra: { selectedOrderItems } };
    } catch (err) {
      return handleRejectedResponse(err, 'Get Add SKUs List', rejectWithValue);
    }
  }
);

export const GetWorkOrderDetails = createAsyncThunk(
  'wo/work-order-details',
  async (workOrderId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/work-order-details/${workOrderId}`
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Work Order Details', rejectWithValue);
    }
  }
);

export const GetGatedStatus = createAsyncThunk(
  'wo/check-gated-status',
  async (args, { rejectWithValue }) => {
    try {
      const { skuId, type, workOrderId } = typeof args === 'object' ? args : { skuId: args, type: null, workOrderId: null };
      const url = `${process.env.OPULENT_NODE_API_URL}/wo/check-gated-status/${skuId}?${type ? `type=${type}&` : ''}${workOrderId ? `workOrderId=${workOrderId}` : ''}`;
      const response = await axios.get(url);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Gated Status', rejectWithValue);
    }
  }
);

export const SyncSkuFullInfo = createAsyncThunk(
  'wo/sync-sku-full-info',
  async ({ workOrderId, skuId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/wo/sync-sku-full-info/${skuId}`,
        {
          params: { workOrderId }
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Sync Full Info', rejectWithValue);
    }
  }
);


export const DeleteOrderItem = createAsyncThunk(

  'wo/delete-order-item/:order_item_id',
  async (orderItemId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/wo/delete-order-item/${orderItemId}`
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Delete Work Order Item', rejectWithValue);
    }
  }
);

export const BulkDeleteOrderItems = createAsyncThunk(
  'wo/bulk-delete-items',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/wo/bulk-delete-items`, {
        data
      });
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Bulk Delete Order Item(s)', rejectWithValue);
    }
  }
);

export const DeleteWorkOrder = createAsyncThunk(
  'wo/delete-work-order/:work_order_id',
  async (workOrderId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(
        `${process.env.OPULENT_NODE_API_URL}/wo/delete-work-order/${workOrderId}`
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Delete Work Order', rejectWithValue);
    }
  }
);

export const CreateInboundPlan = createAsyncThunk(
  'inbound-shipment/create-inbound-plan',
  async ({ workOrderId, actualPlan, packingMethod = 'pack_later', shipmentType = 'SP', items = [] }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/inbound-shipment/create-inbound-plan?workOrderId=${workOrderId}&actualPlan=${actualPlan}&packingMethod=${packingMethod}&shipmentType=${shipmentType}`,
        { items }
      );
      const { packingGroups, packingOptionId } = response?.data || {};
      dispatch(setPackingPreferences({ packingMethod, shipmentType, packingGroups, packingOptionId }));
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Create Inbound Plan', rejectWithValue);
    }
  }
);

export const UpdatePrepCategory = createAsyncThunk(
  'wo/update-prep-category',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/update-prep-category`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Prep Category', rejectWithValue, false);
    }
  }
);

// The nightly sync-prep-owner job owns label_owner + the expiration flags account-wide, so nothing
// is fetched from Amazon on page load. This is the one exception: SKUs the user JUST ADDED to a
// work order wouldn't have them until the next nightly run, and both matter immediately (a wrong
// label owner makes Amazon reject the plan; a missing expiry flag hides the "date required" marker).
// Scoped to the added ids and to NULL values only, so it's ~1-2 Amazon calls and never clobbers a
// manual pick. Returns the resolved flags so the grid can be patched without a refetch.
export const SyncSkuAmazonFlags = createAsyncThunk(
  'wo/sync-sku-amazon-flags',
  async ({ skuIds = [] }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/wo/sync-sku-amazon-flags`,
        { skuIds }
      );
      return response?.data; // { success, data: { flags: [{ sku_id, label_owner, ... }], updated } }
    } catch (err) {
      return handleRejectedResponse(err, 'Sync SKU Flags', rejectWithValue, false);
    }
  }
);

export const UpdatePrepOwner = createAsyncThunk(
  'wo/update-prep-owner',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/update-prep-owner`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Prep Owner', rejectWithValue);
    }
  }
);

// Manual override of the per-SKU Amazon label owner (seller_skus.label_owner) — normally synced
// from listPrepDetails, but the operator can set it from the work-order grid.
export const UpdateLabelOwner = createAsyncThunk(
  'wo/update-label-owner',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/wo/update-label-owner`,
        data
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Label Owner', rejectWithValue);
    }
  }
);

export const ProcessBulkWorkOrderItem = createAsyncThunk(
  'wo/processBulkWorkOrderItem',
  async (body, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/wo/bulk-work-order-item`,
        body
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Uploading File', rejectWithValue);
    }
  }
);

const workOrders = createSlice({
  name: 'workOrders',
  initialState: {
    creatingWorkOrder: false,
    mergingWorkOrders: false,

    userWorkOrders: [],
    fetchingUserWorkOrders: false,
    userWorkOrdersCount: 0,
    userWorkOrderAllStatusCount: {},

    warehouseWorkOrders: [],
    fetchingWarehouseWorkOrders: false,
    warehouseWorkOrdersCount: 0,
    warehouseWorkOrderAllStatusCount: {},

    fetchingWorkOrderDetails: false,
    workOrderDetails: {},

    fetchingOrderItems: false,
    syncingSkuFlags: false,
    orderItems: [],
    orderItemsCount: 0,

    fetchingRemainingSkuList: false,
    remainingSkus: [],
    remainingSkusCount: 0,

    fetchingWarehouseAddresses: false,
    warehouseAddresses: [],

    fetchingAllUsers: false,
    usersList: [],

    fetchingSkuGatedStatus: false,
    updatingShipQuantity: false,
    updatingOrderDetails: false,
    deletingWorkOrder: false,
    deletingOrderItem: false,
    addingOrderItems: false,
    updatingOrderItem: false,

    creatingInboundPlan: false,
    invalidSkuMap: [],
    inboundPlanId: '',

    updatingPrepCategory: false,
    processWorkOrderLoading: false,
    syncingSkuFullInfo: false,

    bulkDeletingOrderItems: false,
    updatingPrepOwner: false,
    orderItemExpiryLoading: false,
    actualPlan: false
  },
  reducers: {
    setUpdatedSkuStatus(state, { payload: { field, index, value } }) {
      state[field][index]['sku_status'] = value;
    },
    updateExpiryDate(state, { payload: { index, value } }) {
      state['orderItems'][index]['work_order_items_v2s'][0]['expiry_date'] = value;
    },
    updateState(state, { payload: { key, value } }) {
      state[key] = value;
    },
    editPrepCategory(state, { payload: { id, value } }) {
      const index = state['orderItems'].findIndex(item => item.id === id);
      state['orderItems'][index]['prep_category'] = value;
    },
    editPrepOwner(state, { payload: { id, value } }) {
      const index = state['orderItems'].findIndex(item => item.id === id);
      state['orderItems'][index]['prep_owner'] = value;
    },
    editLabelOwner(state, { payload: { id, value } }) {
      const index = state['orderItems'].findIndex(item => item.id === id);
      state['orderItems'][index]['label_owner'] = value;
    },
    updateShipQty(state, { payload: { field, index, value } }) {
      state[field][index]['ship_quantity'] = value;
    },
    updateGridShippedQty(state, { payload: { field, index, value } }) {
      state[field][index]['work_order_items_v2s'][0]['ship_quantity'] = value;
      state[field][index]['work_order_items_v2s'][0]['is_errored'] = false;
    },
    removeDataEntry(state, { payload: { field, key, value } }) {
      state[field] = filter(state[field], obj => (obj[key] !== value));
    },
    appendErrorMessages(state) {
      if (!state.orderItems) return;
      state.orderItems = state.orderItems.map(item => {
        const errorMessages = state.invalidSkuMap[item.sku];
        return {
          ...item,
          error_msg: errorMessages ? errorMessages[0] : ""
        };
      });
    },
    resetWorkOrderState(state) {
      state.orderItems = [];
      state.orderItemsCount = 0;
      state.workOrderDetails = {};
      state.inboundPlanId = '';
      state.invalidSkuMap = [];
      state.actualPlan = false;
    },
  },
  extraReducers: {
    [GetAllUsers.pending]: (state) => {
      state.fetchingAllUsers = true;
    },
    [GetAllUsers.fulfilled]: (state, action) => {
      const { data } = action?.payload || {};

      state.usersList = data?.map(({ id, first_name }) => ({ value: id, label: first_name }));
      state.fetchingAllUsers = false;
    },
    [GetAllUsers.rejected]: (state) => {
      state.fetchingAllUsers = false;
    },

    [GetAllShipFromAddress.pending]: (state) => {
      state.fetchingWarehouseAddresses = true;
    },
    [GetAllShipFromAddress.fulfilled]: (state, action) => {
      const { data } = action?.payload || {};

      state.warehouseAddresses = data;
      state.fetchingWarehouseAddresses = false;
    },
    [GetAllShipFromAddress.rejected]: (state) => {
      state.fetchingWarehouseAddresses = false;
    },

    [GetUserWorkOrders.pending]: (state) => {
      state.fetchingUserWorkOrders = true;
    },
    [GetUserWorkOrders.fulfilled]: (state, action) => {
      const { data, count, statusCounts } = action?.payload || {};

      state.userWorkOrderAllStatusCount = statusCounts || {}
      state.userWorkOrders = data;
      state.fetchingUserWorkOrders = false;
      state.userWorkOrdersCount = count;
    },
    [GetUserWorkOrders.rejected]: (state) => {
      state.fetchingUserWorkOrders = false;
    },

    [GetGatedStatus.pending]: (state) => {
      state.fetchingSkuGatedStatus = true;
    },
    [GetGatedStatus.fulfilled]: (state) => {
      state.fetchingSkuGatedStatus = false;
    },
    [GetGatedStatus.rejected]: (state) => {
      state.fetchingSkuGatedStatus = false;
    },

    [SyncSkuFullInfo.pending]: (state) => {
      state.syncingSkuFullInfo = true;
    },
    [SyncSkuFullInfo.fulfilled]: (state, action) => {
      const { data } = action?.payload || {};
      const index = state.orderItems.findIndex(item => item.id === data?.id);
      if (index !== -1) {
        state.orderItems[index] = { ...state.orderItems[index], ...data };
      }
      state.syncingSkuFullInfo = false;
    },

    [SyncSkuFullInfo.rejected]: (state) => {
      state.syncingSkuFullInfo = false;
    },

    [GetRemainingSkuList.pending]: (state) => {
      state.fetchingRemainingSkuList = true;
    },
    [GetRemainingSkuList.fulfilled]: (state, action) => {
      const { data, count } = action?.payload || {};
      const { selectedOrderItems } = action?.payload?.extra || {}

      let dataCopy = [...data]

      dataCopy = dataCopy.map(item => {
        const matchedSelected = selectedOrderItems?.find(
          selected => selected?.sku === item?.sku
        );

        if (matchedSelected) {

          const updatedProductsV2 = (matchedSelected.products_v2 || []).map((product) => {
            const reservedQty = product?.reserved_qty || 0;
            const warehouseStock = product?.warehouse_stock || 0;

            return {
              ...product,
              reserved_qty: reservedQty,
              warehouse_stock: warehouseStock,
            };
          });

          return {
            ...item,
            ship_quantity: matchedSelected?.ship_quantity || item?.ship_quantity || 0,
            reserved_qty: matchedSelected?.reserved_qty || item?.reserved_qty,
            total_quantity: matchedSelected?.total_quantity || item?.total_quantity,
            warehouse_stock: matchedSelected?.warehouse_stock || item?.warehouse_stock,
            products_v2: updatedProductsV2,
          };
        }

        return item;
      });

      state.remainingSkusCount = count;
      state.remainingSkus = dataCopy;
      state.fetchingRemainingSkuList = false;
    },
    [GetRemainingSkuList.rejected]: (state) => {
      state.fetchingRemainingSkuList = false;
    },

    // Amazon flag resolution for newly added SKUs — surfaced so the page can show a loader
    // instead of leaving a stale grid on screen while the SP-API calls run.
    [SyncSkuAmazonFlags.pending]: (state) => {
      state.syncingSkuFlags = true;
    },
    [SyncSkuAmazonFlags.fulfilled]: (state) => {
      state.syncingSkuFlags = false;
    },
    [SyncSkuAmazonFlags.rejected]: (state) => {
      state.syncingSkuFlags = false;
    },

    [GetOrderItems.pending]: (state) => {
      state.fetchingOrderItems = true;
    },
    [GetOrderItems.fulfilled]: (state, action) => {
      const { exportAll } = action?.meta?.arg || {};
      if (!exportAll) {
        const { data, count } = action?.payload || {};
        state.orderItemsCount = count;
        state.orderItems = data || [];
        state.fetchingOrderItems = false;
      } else {
        state.fetchingOrderItems = false;
      }
    },
    [GetOrderItems.rejected]: (state) => {
      state.fetchingOrderItems = false;
    },

    [GetWorkOrderDetails.pending]: (state) => {
      state.fetchingWorkOrderDetails = true;
    },
    [GetWorkOrderDetails.fulfilled]: (state, action) => {
      const { data } = action?.payload || {};

      state.fetchingWorkOrderDetails = false;
      state.workOrderDetails = data;
    },
    [GetWorkOrderDetails.rejected]: (state) => {
      state.fetchingWorkOrderDetails = false;
    },

    [GetWarehouseWorkOrders.pending]: (state) => {
      state.fetchingWarehouseWorkOrders = true;
    },
    [GetWarehouseWorkOrders.fulfilled]: (state, action) => {
      const { data, count, statusCounts } = action?.payload || {};

      state.warehouseWorkOrderAllStatusCount = statusCounts || {};
      state.warehouseWorkOrders = data;
      state.fetchingWarehouseWorkOrders = false;
      state.warehouseWorkOrdersCount = count;
    },
    [GetWarehouseWorkOrders.rejected]: (state) => {
      state.fetchingWarehouseWorkOrders = false;
    },

    [AddOrderItems.pending]: (state) => {
      state.addingOrderItems = true;
    },
    [AddOrderItems.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.addingOrderItems = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [AddOrderItems.rejected]: (state) => {
      state.addingOrderItems = false;
    },

    [BulkDeleteOrderItems.pending]: (state) => {
      state.bulkDeletingOrderItems = true;
    },
    [BulkDeleteOrderItems.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.bulkDeletingOrderItems = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [BulkDeleteOrderItems.rejected]: (state) => {
      state.bulkDeletingOrderItems = false;
    },

    [DeleteOrderItem.pending]: (state) => {
      state.deletingOrderItem = true;
    },
    [DeleteOrderItem.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.deletingOrderItem = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [DeleteOrderItem.rejected]: (state) => {
      state.deletingOrderItem = false;
    },

    [DeleteWorkOrder.pending]: (state) => {
      state.deletingWorkOrder = true;
    },
    [DeleteWorkOrder.fulfilled]: (state, action) => {
      const { message, success, statusCounts } = action?.payload || {};
      state.userWorkOrderAllStatusCount = statusCounts || {};

      state.deletingWorkOrder = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [DeleteWorkOrder.rejected]: (state) => {
      state.deletingWorkOrder = false;
    },

    [UpdateOrderItem.pending]: (state) => {
      state.updatingOrderItem = true;
    },
    [UpdateOrderItem.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingOrderItem = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [UpdateOrderItem.rejected]: (state) => {
      state.updatingOrderItem = false;
    },

    [EditBulkOrderItemExpiry.pending]: (state) => {
      state.orderItemExpiryLoading = true;
    },
    [EditBulkOrderItemExpiry.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.orderItemExpiryLoading = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [EditBulkOrderItemExpiry.rejected]: (state) => {
      state.orderItemExpiryLoading = false;
    },

    [UpdateOrderDetails.pending]: (state) => {
      state.updatingOrderDetails = true;
    },
    [UpdateOrderDetails.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingOrderDetails = false;
      // notification[success ? 'success' : 'error']({
      //   message,
      //   top: 65,
      // });
    },
    [UpdateOrderDetails.rejected]: (state) => {
      state.updatingOrderDetails = false;
    },

    [UpdatePrepCategory.pending]: (state) => {
      state.updatingPrepCategory = true;
    },
    [UpdatePrepCategory.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingPrepCategory = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [UpdatePrepCategory.rejected]: (state) => {
      state.updatingPrepCategory = false;
    },

    [UpdateShipQuantity.pending]: (state) => {
      state.updatingShipQuantity = true;
    },
    [UpdateShipQuantity.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingShipQuantity = false;
    },
    [UpdateShipQuantity.rejected]: (state) => {
      state.updatingShipQuantity = false;
    },

    [CreateWorkOrder.pending]: (state) => {
      state.creatingWorkOrder = true;
    },
    [CreateWorkOrder.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.creatingWorkOrder = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [CreateWorkOrder.rejected]: (state) => {
      state.creatingWorkOrder = false;
    },

    [MergeWorkOrders.pending]: (state) => {
      state.mergingWorkOrders = true;
    },
    [MergeWorkOrders.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};
      state.mergingWorkOrders = false;
      if (message) {
        notification[success ? 'success' : 'error']({
          message,
          top: 65,
        });
      }
    },
    [MergeWorkOrders.rejected]: (state) => {
      state.mergingWorkOrders = false;
    },

    [CreateInboundPlan.pending]: (state) => {
      state.creatingInboundPlan = true;
    },
    [CreateInboundPlan.fulfilled]: (state, action) => {
      const { message, success, inboundPlanId } = action?.payload || {};
      const { packingMethod, actualPlan } = action?.meta?.arg || {};

      // Component handles its own success toast for pack_now actualPlan flow (SP and LTL);
      // skip the slice-level toast in that case to avoid a duplicate.
      if (!(success && packingMethod === 'pack_now' && actualPlan)) {
        notification[success ? 'success' : 'error']({
          message,
          top: 65,
        });
      }

      state.inboundPlanId = inboundPlanId;
      state.creatingInboundPlan = false;
    },
    [CreateInboundPlan.rejected]: (state, action) => {
      const { invalidSkus } = action?.payload || {};

      state.creatingInboundPlan = false;
      if (!isEmpty(invalidSkus)) {
        // creating a single object for fast searching
        const invalidSkuMap = invalidSkus.reduce((acc, obj) => {
          return { ...acc, ...obj };
        }, {});
        state.invalidSkuMap = invalidSkuMap;
      }
      else {
        state.invalidSkuMap = [];
      }
    },
    [ProcessBulkWorkOrderItem.pending]: (state) => ({
      ...state,
      processWorkOrderLoading: true
    }),
    [ProcessBulkWorkOrderItem.fulfilled]: (state, { payload }) => {
      state.processWorkOrderLoading = false;
      state.success = true;
      state.message = payload.message;
    },
    [ProcessBulkWorkOrderItem.rejected]: (state, { payload }) => ({
      ...state,
      err: payload?.error?.error || payload?.error,
      processWorkOrderLoading: false
    }),
    [UpdatePrepOwner.pending]: (state) => {
      state.updatingPrepOwner = true;
    },
    [UpdatePrepOwner.fulfilled]: (state, action) => {
      const { message, success } = action?.payload || {};

      state.updatingPrepOwner = false;
      notification[success ? 'success' : 'error']({
        message,
        top: 65,
      });
    },
    [UpdatePrepOwner.rejected]: (state) => {
      state.updatingPrepOwner = false;
    },
  },
});

const { reducer, actions } = workOrders;

export const { updateGridShippedQty, updateShipQty, removeDataEntry, updateState, setUpdatedSkuStatus, updateExpiryDate, appendErrorMessages, editPrepCategory, editPrepOwner, editLabelOwner, resetWorkOrderState } = actions;

export default reducer;
